import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Star, TrendingUp, DollarSign, BarChart3, ShieldCheck, Clock, MapPin, User } from 'lucide-react';
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/1UzPp3X8zdmUnXGa5Hgbnx/Diseno_sin_titulo_(9).png';
// Fake purchase data for the popup
const PURCHASES = [{
  name: 'Juan',
  city: 'Córdoba'
}, {
  name: 'Matias',
  city: 'Buenos Aires'
}, {
  name: 'Federico',
  city: 'Rosario'
}, {
  name: 'Lucas',
  city: 'Mendoza'
}, {
  name: 'Ezequiel',
  city: 'Tucumán'
}, {
  name: 'Gastón',
  city: 'Mar del Plata'
}, {
  name: 'Alejandro',
  city: 'Salta'
}, {
  name: 'Nicolás',
  city: 'Neuquén'
}];
const TESTIMONIALS = [{
  name: 'Matias R.',
  location: 'Buenos Aires',
  text: 'Desde que uso el Kit Barbero Pro, por fin sé cuánto me queda limpio a fin de mes. Antes era todo a ojo. ¡Un golazo!'
}, {
  name: 'Federico L.',
  location: 'Rosario',
  text: 'Excelente sistema. Me di cuenta que estaba perdiendo plata con algunos cortes. Lo apliqué y en una semana acomodé los precios sin perder clientes.'
}, {
  name: 'Lucas G.',
  location: 'Córdoba',
  text: 'Trabajaba 12 horas por día y la plata no rendía. Con esta herramienta ordené los números de la barbería y ahora tengo tranquilidad.'
}, {
  name: 'Ezequiel M.',
  location: 'Mendoza',
  text: 'Muy fácil de usar, no necesitás ser contador. Lo abro en la compu del local y anoto todo ahí. 100% recomendado para colegas.'
}];
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.5,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
const GoldDivider = () => <div className="w-full flex justify-center py-4 md:py-6" data-id="element-1">
    <div className="h-px w-3/4 max-w-3xl bg-gradient-to-r from-transparent via-gold to-transparent opacity-40" data-id="element-2" />
  </div>;
export function App() {
  const [showPopup, setShowPopup] = useState(false);
  const [currentPurchase, setCurrentPurchase] = useState(PURCHASES[0]);
  const offerRef = useRef<HTMLDivElement>(null);
  const scrollToOffer = (e: React.MouseEvent) => {
    e.preventDefault();
    offerRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  // Meta Pixel - PageView
  useEffect(() => {
    const PIXEL_ID = '26257470350562748';
    // Prevent duplicate initialization
    if ((window as any).fbq) return;
    const f = window as any;
    const n = f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    f.fbq('init', PIXEL_ID);
    f.fbq('track', 'PageView');
    // noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      const randomPurchase = PURCHASES[Math.floor(Math.random() * PURCHASES.length)];
      setCurrentPurchase(randomPurchase);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
      }, 4000);
    }, 15000);
    return () => clearInterval(interval);
  }, []);
  return <div className="min-h-screen bg-black text-white selection:bg-gold selection:text-black" data-id="element-3">
      {/* Fake Purchase Popup */}
      <AnimatePresence data-id="element-4">
        {showPopup && <motion.div initial={{
        opacity: 0,
        y: 50,
        x: -20
      }} animate={{
        opacity: 1,
        y: 0,
        x: 0
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className="fixed bottom-4 left-4 z-50 bg-dark-card border border-gold/30 p-3 rounded-lg shadow-gold-glow flex items-center gap-3 max-w-xs" data-id="element-5">
            <div className="bg-gold/20 p-2 rounded-full" data-id="element-6">
              <Check className="text-gold w-5 h-5" data-id="element-7" />
            </div>
            <div data-id="element-8">
              <p className="text-sm text-gray-300" data-id="element-9">
                <span className="font-bold text-white" data-id="element-10">
                  {currentPurchase.name}
                </span>{' '}
                de {currentPurchase.city}
              </p>
              <p className="text-xs text-gold font-semibold mt-0.5" data-id="element-11">
                Acaba de comprar Kit Barbero Pro
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* 1. HERO SECTION */}
      <section className="relative pt-12 md:pt-20 pb-8 md:pb-12 px-4 overflow-hidden" data-id="element-12">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[400px] bg-gold/10 blur-[120px] rounded-full pointer-events-none" data-id="element-13" />

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-14">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5
        }} className="inline-block border border-gold/50 bg-gold/10 text-gold px-3 py-1 rounded-full text-xs md:text-sm font-bold tracking-wider mb-4 md:mb-6 uppercase" data-id="element-15">
            💈 PARA DUEÑOS DE BARBERÍAS QUE QUIEREN DEJAR DE PERDER DINERO
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-4xl md:text-7xl font-bebas leading-tight mb-4" data-id="element-16">
            DEJÁ DE TRABAJAR TODO EL DÍA{' '}
            <span className="text-gold block mt-1" data-id="element-17">
              SIN SABER CUÁNTO GANÁS REALMENTE
            </span>
          </motion.h1>

          {/* Product image right below the title */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative mx-auto max-w-xs md:max-w-lg mb-6 md:mb-8 cursor-pointer group" onClick={scrollToOffer} data-id="element-18">
            <div className="absolute inset-0 bg-gold/20 blur-3xl rounded-full group-hover:bg-gold/30 transition-all duration-500" data-id="element-19" />
            <motion.img animate={{
            y: [0, -12, 0]
          }} transition={{
            repeat: Infinity,
            duration: 4,
            ease: 'easeInOut'
          }} src={PRODUCT_IMAGE} alt="Kit Barbero Pro" className="relative z-10 w-full drop-shadow-[0_0_30px_rgba(201,161,74,0.4)] transition-transform duration-500 group-hover:scale-105" data-id="element-20" />
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="text-base md:text-xl text-gray-300 mb-6 max-w-3xl mx-auto leading-relaxed" data-id="element-21">
            El primer sistema de gestión financiera diseñado específicamente
            para barberías que te permite saber cuánto ganás por corte y tomar
            control total de tu negocio desde el primer día.
          </motion.p>

          <FadeIn delay={0.4} data-id="element-22">
            <h2 className="text-3xl md:text-5xl font-bebas text-white mb-2 tracking-wide" data-id="element-23">
              KIT BARBERO PRO
            </h2>
            <p className="text-lg md:text-xl text-gold font-medium mb-5" data-id="element-24">
              Gestión financiera para dueños de barberías: dejá de perder dinero
              con cada corte desde el primer día.
            </p>

            <div className="flex flex-col md:flex-row justify-center items-center gap-2 md:gap-8 mb-6 text-gray-300 font-medium text-sm md:text-base" data-id="element-25">
              <div className="flex items-center gap-2" data-id="element-26">
                <Check className="text-gold w-4 h-4 md:w-5 md:h-5" data-id="element-27" /> Sistema
                simple
              </div>
              <div className="flex items-center gap-2" data-id="element-28">
                <Check className="text-gold w-4 h-4 md:w-5 md:h-5" data-id="element-29" /> Sin
                contabilidad complicada
              </div>
              <div className="flex items-center gap-2" data-id="element-30">
                <Check className="text-gold w-4 h-4 md:w-5 md:h-5" data-id="element-31" /> Aplicable
                en minutos
              </div>
            </div>

            <button onClick={scrollToOffer} className="w-full md:w-auto bg-gradient-to-r from-gold-dark via-gold to-gold-light text-black font-bold text-lg md:text-2xl py-4 px-8 rounded-lg shadow-gold-glow hover:shadow-gold-glow-lg transition-all transform hover:-translate-y-1 flex items-center justify-center gap-3 mx-auto" data-id="element-32">
              👉 ACCEDER AL KIT AHORA
            </button>
            <p className="text-gray-400 mt-3 text-xs md:text-sm font-medium tracking-widest uppercase" data-id="element-33">
              Pago único · Acceso inmediato
            </p>
          </FadeIn>
        </div>
      </section>

      <GoldDivider data-id="element-34" />

      {/* 2. PAIN POINTS */}
      <section className="py-8 md:py-12 px-4" data-id="element-35">
        <div className="max-w-3xl mx-auto" data-id="element-36">
          <FadeIn data-id="element-37">
            <h2 className="text-2xl md:text-5xl font-bebas text-center mb-6 md:mb-10 text-white" data-id="element-38">
              SI TENÉS UNA BARBERÍA,{' '}
              <span className="text-gold" data-id="element-39">ESTO TE VA A SONAR FAMILIAR</span>
            </h2>
          </FadeIn>

          <div className="space-y-3 md:space-y-5 text-base md:text-xl text-gray-300 leading-relaxed border-l-2 border-gold/30 pl-4 md:pl-10" data-id="element-40">
            <FadeIn delay={0.1} data-id="element-41">
              <p data-id="element-42">
                Terminás el día cansado. Trabajaste todo el día. La barbería
                estuvo llena.
              </p>
            </FadeIn>
            <FadeIn delay={0.15} data-id="element-43">
              <p data-id="element-44">Pero cuando mirás la plata…</p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-45">
              <p className="text-xl md:text-2xl font-bold text-white" data-id="element-46">
                No alcanza.
              </p>
            </FadeIn>
            <FadeIn delay={0.25} data-id="element-47">
              <p data-id="element-48">
                No sabés cuánto ganaste realmente. No sabés si estás cobrando
                bien. No sabés si tu negocio está creciendo o sobreviviendo.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-49">
              <p className="text-gold font-semibold mt-4" data-id="element-50">Y lo peor:</p>
            </FadeIn>
            <FadeIn delay={0.35} data-id="element-51">
              <p data-id="element-52">Cada decisión la tomás "a ojo".</p>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-53">
              <p data-id="element-54">Precios. Gastos. Inversiones.</p>
            </FadeIn>
            <FadeIn delay={0.45} data-id="element-55">
              <p className="font-bold text-white" data-id="element-56">Todo sin claridad.</p>
            </FadeIn>
          </div>
        </div>
      </section>

      <GoldDivider data-id="element-57" />

      {/* 3. PROBLEM */}
      <section className="py-8 md:py-12 px-4 bg-dark-card/50" data-id="element-58">
        <div className="max-w-3xl mx-auto text-center" data-id="element-59">
          <FadeIn data-id="element-60">
            <h2 className="text-3xl md:text-5xl font-bebas text-white mb-4 md:mb-6" data-id="element-61">
              EL PROBLEMA <span className="text-gold" data-id="element-62">NO ES TU BARBERÍA</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 mb-4" data-id="element-63">
              Tu barbería funciona.
            </p>
            <div className="bg-gold/10 border border-gold/30 rounded-xl p-4 md:p-6 mb-6 inline-block" data-id="element-64">
              <p className="text-lg md:text-2xl font-bold text-gold" data-id="element-65">
                👉 El problema es que no estás gestionando el dinero.
              </p>
            </div>
            <p className="text-lg md:text-xl text-white font-semibold mb-4" data-id="element-66">
              Estás improvisando.
            </p>
            <p className="text-gray-400 mb-4 uppercase tracking-widest text-xs md:text-sm" data-id="element-67">
              Y eso genera:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-left max-w-2xl mx-auto" data-id="element-68">
              <div className="bg-black border border-gray-800 p-3 rounded-lg flex items-start gap-2" data-id="element-69">
                <X className="text-red-500 w-5 h-5 shrink-0 mt-0.5" data-id="element-70" />
                <span className="text-sm md:text-base text-gray-300" data-id="element-71">
                  Meses buenos… seguidos de meses malos
                </span>
              </div>
              <div className="bg-black border border-gray-800 p-3 rounded-lg flex items-start gap-2" data-id="element-72">
                <X className="text-red-500 w-5 h-5 shrink-0 mt-0.5" data-id="element-73" />
                <span className="text-sm md:text-base text-gray-300" data-id="element-74">
                  Miedo a subir precios
                </span>
              </div>
              <div className="bg-black border border-gray-800 p-3 rounded-lg flex items-start gap-2" data-id="element-75">
                <X className="text-red-500 w-5 h-5 shrink-0 mt-0.5" data-id="element-76" />
                <span className="text-sm md:text-base text-gray-300" data-id="element-77">
                  Falta de crecimiento
                </span>
              </div>
              <div className="bg-black border border-gray-800 p-3 rounded-lg flex items-start gap-2" data-id="element-78">
                <X className="text-red-500 w-5 h-5 shrink-0 mt-0.5" data-id="element-79" />
                <span className="text-sm md:text-base text-gray-300" data-id="element-80">
                  Sensación constante de estancamiento
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      <GoldDivider data-id="element-81" />

      {/* 4. SOLUTION */}
      <section className="py-8 md:py-12 px-4" data-id="element-82">
        <div className="max-w-5xl mx-auto" data-id="element-83">
          <FadeIn data-id="element-84">
            <div className="text-center mb-6 md:mb-10" data-id="element-85">
              <h2 className="text-3xl md:text-6xl font-bebas text-white mb-2" data-id="element-86">
                UN SISTEMA QUE TE DA{' '}
                <span className="text-gold" data-id="element-87">CONTROL REAL</span>
              </h2>
              <h3 className="text-2xl md:text-3xl font-bebas text-gold tracking-widest mb-3" data-id="element-88">
                KIT BARBERO PRO
              </h3>
              <p className="text-base md:text-xl text-gray-300 max-w-2xl mx-auto" data-id="element-89">
                Un sistema práctico que transforma números en decisiones claras.
              </p>
            </div>
          </FadeIn>

          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-12" data-id="element-90">
            <div className="w-full md:w-1/2 cursor-pointer group" onClick={scrollToOffer} data-id="element-91">
              <motion.img whileHover={{
              scale: 1.05
            }} transition={{
              duration: 0.4
            }} src={PRODUCT_IMAGE} alt="Kit Barbero Pro" className="w-full max-w-xs md:max-w-md mx-auto drop-shadow-gold-glow" data-id="element-92" />
            </div>
            <div className="w-full md:w-1/2" data-id="element-93">
              <p className="text-lg md:text-xl font-bold text-white mb-4 md:mb-6" data-id="element-94">
                Entrás a tu barbería sabiendo:
              </p>
              <div className="space-y-3" data-id="element-95">
                {['Cuánto ganás por corte', 'Cuánto necesitás facturar por día', 'Qué precios tenés que cobrar', 'Dónde se está yendo tu dinero'].map((item, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-96">
                    <div className="flex items-center gap-3 bg-dark-card border border-gold/20 p-3 rounded-lg hover:border-gold/50 transition-colors" data-id="element-97">
                      <div className="bg-gold/10 p-1.5 rounded-full" data-id="element-98">
                        <Check className="text-gold w-5 h-5" data-id="element-99" />
                      </div>
                      <span className="text-base md:text-lg text-gray-200 font-medium" data-id="element-100">
                        {item}
                      </span>
                    </div>
                  </FadeIn>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      <GoldDivider data-id="element-101" />

      {/* 5. WHAT'S INCLUDED */}
      <section className="py-8 md:py-12 px-4 bg-dark-card/30" data-id="element-102">
        <div className="max-w-4xl mx-auto" data-id="element-103">
          <FadeIn data-id="element-104">
            <h2 className="text-3xl md:text-5xl font-bebas text-center text-white mb-3" data-id="element-105">
              QUÉ INCLUYE <span className="text-gold" data-id="element-106">EL KIT</span>
            </h2>
            <p className="text-base md:text-xl text-center text-gray-300 mb-6 md:mb-10" data-id="element-107">
              Todo lo necesario para que tu barbería deje de improvisar:
            </p>
          </FadeIn>

          <div className="grid grid-cols-2 gap-3 md:gap-6" data-id="element-108">
            <FadeIn delay={0.1} data-id="element-109">
              <div className="bg-black border border-gold/30 p-4 md:p-8 rounded-xl h-full hover:shadow-gold-glow transition-shadow" data-id="element-110">
                <div className="text-2xl md:text-4xl mb-2" data-id="element-111">📊</div>
                <h3 className="text-sm md:text-xl font-bold text-white" data-id="element-112">
                  Sistema de cálculo de costo por corte
                </h3>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-113">
              <div className="bg-black border border-gold/30 p-4 md:p-8 rounded-xl h-full hover:shadow-gold-glow transition-shadow" data-id="element-114">
                <div className="text-2xl md:text-4xl mb-2" data-id="element-115">💰</div>
                <h3 className="text-sm md:text-xl font-bold text-white" data-id="element-116">
                  Método de precios rentables
                </h3>
              </div>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-117">
              <div className="bg-black border border-gold/30 p-4 md:p-8 rounded-xl h-full hover:shadow-gold-glow transition-shadow" data-id="element-118">
                <div className="text-2xl md:text-4xl mb-2" data-id="element-119">📉</div>
                <h3 className="text-sm md:text-xl font-bold text-white" data-id="element-120">
                  Control simple de ingresos y gastos
                </h3>
              </div>
            </FadeIn>
            <FadeIn delay={0.4} data-id="element-121">
              <div className="bg-black border border-gold/30 p-4 md:p-8 rounded-xl h-full hover:shadow-gold-glow transition-shadow" data-id="element-122">
                <div className="text-2xl md:text-4xl mb-2" data-id="element-123">📈</div>
                <h3 className="text-sm md:text-xl font-bold text-white" data-id="element-124">
                  Herramientas para escalar sin perder dinero
                </h3>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 6. BONUSES */}
      <section className="py-8 md:py-12 px-4 relative overflow-hidden" data-id="element-125">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gold/5 blur-[150px] rounded-full pointer-events-none" data-id="element-126" />
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-127">
          <FadeIn data-id="element-128">
            <div className="text-center mb-6 md:mb-10" data-id="element-129">
              <h2 className="text-3xl md:text-5xl font-bebas text-white mb-3" data-id="element-130">
                BONOS INCLUIDOS
              </h2>
              <div className="inline-block bg-gold text-black font-bold px-4 py-1.5 rounded-full text-xs md:text-base animate-pulse" data-id="element-131">
                GRATIS POR EL DÍA DE HOY
              </div>
            </div>
          </FadeIn>

          <div className="space-y-3" data-id="element-132">
            <FadeIn delay={0.1} data-id="element-133">
              <div className="flex items-center gap-4 bg-gradient-to-r from-dark-card to-black border border-gold/40 p-4 rounded-xl" data-id="element-134">
                <div className="text-2xl md:text-4xl" data-id="element-135">🎁</div>
                <h3 className="text-base md:text-2xl font-bold text-gold" data-id="element-136">
                  Calculadora de ingresos
                </h3>
              </div>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-137">
              <div className="flex items-center gap-4 bg-gradient-to-r from-dark-card to-black border border-gold/40 p-4 rounded-xl" data-id="element-138">
                <div className="text-2xl md:text-4xl" data-id="element-139">🎁</div>
                <h3 className="text-base md:text-2xl font-bold text-gold" data-id="element-140">
                  Sistema de organización financiera
                </h3>
              </div>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-141">
              <div className="flex items-center gap-4 bg-gradient-to-r from-dark-card to-black border border-gold/40 p-4 rounded-xl" data-id="element-142">
                <div className="text-2xl md:text-4xl" data-id="element-143">🎁</div>
                <h3 className="text-base md:text-2xl font-bold text-gold" data-id="element-144">
                  Guía de control de gastos
                </h3>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      <GoldDivider data-id="element-145" />

      {/* 7. FOR YOU / NOT FOR YOU */}
      <section className="py-8 md:py-12 px-4" data-id="element-146">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8" data-id="element-147">
          <FadeIn data-id="element-148">
            <div className="bg-dark-card border border-gold/30 rounded-2xl p-5 md:p-8 h-full" data-id="element-149">
              <h2 className="text-2xl md:text-3xl font-bebas text-white mb-4 md:mb-6 flex items-center gap-3" data-id="element-150">
                <Check className="text-gold w-7 h-7" data-id="element-151" /> ESTO ES PARA VOS SI
              </h2>
              <ul className="space-y-3" data-id="element-152">
                {['Tenés una barbería y no sabés cuánto ganás realmente', 'Trabajás mucho pero no ves resultados', 'Querés dejar de improvisar', 'Querés tener control total del negocio', 'Querés crecer sin miedo'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-gray-300" data-id="element-153">
                    <Check className="text-gold w-5 h-5 shrink-0 mt-0.5" data-id="element-154" />
                    <span className="text-sm md:text-lg" data-id="element-155">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-156">
            <div className="bg-black border border-gray-800 rounded-2xl p-5 md:p-8 h-full opacity-80" data-id="element-157">
              <h2 className="text-2xl md:text-3xl font-bebas text-gray-400 mb-4 md:mb-6 flex items-center gap-3" data-id="element-158">
                <X className="text-red-500 w-7 h-7" data-id="element-159" /> ESTO NO ES PARA VOS SI
              </h2>
              <ul className="space-y-3" data-id="element-160">
                {['Creés que trabajar más horas es la solución', 'No querés ver los números de tu negocio', 'Preferís seguir manejándote "como siempre"'].map((item, idx) => <li key={idx} className="flex items-start gap-2 text-gray-500" data-id="element-161">
                    <X className="text-red-500/70 w-5 h-5 shrink-0 mt-0.5" data-id="element-162" />
                    <span className="text-sm md:text-lg" data-id="element-163">{item}</span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>
        </div>
      </section>

      <GoldDivider data-id="element-164" />

      {/* 8. IMMEDIATE ACCESS & 9. RESULT */}
      <section className="py-8 md:py-12 px-4 bg-dark-card/40" data-id="element-165">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12" data-id="element-166">
          <FadeIn data-id="element-167">
            <div data-id="element-168">
              <h2 className="text-3xl md:text-4xl font-bebas text-white mb-4" data-id="element-169">
                ACCESO INMEDIATO
              </h2>
              <p className="text-base md:text-xl text-gray-400 mb-4" data-id="element-170">
                Después de comprar recibís:
              </p>
              <ul className="space-y-3" data-id="element-171">
                <li className="flex items-center gap-2 text-base md:text-lg text-gray-200" data-id="element-172">
                  <Check className="text-gold w-5 h-5" data-id="element-173" /> Acceso inmediato
                </li>
                <li className="flex items-center gap-2 text-base md:text-lg text-gray-200" data-id="element-174">
                  <Check className="text-gold w-5 h-5" data-id="element-175" /> Material listo para
                  usar
                </li>
                <li className="flex items-center gap-2 text-base md:text-lg text-gray-200" data-id="element-176">
                  <Check className="text-gold w-5 h-5" data-id="element-177" /> Sistema aplicable
                  desde el primer día
                </li>
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-178">
            <div className="border-l-2 border-gold/30 pl-6 md:pl-8" data-id="element-179">
              <h2 className="text-3xl md:text-4xl font-bebas text-gold mb-4" data-id="element-180">
                EL RESULTADO
              </h2>
              <div className="space-y-3 text-base md:text-lg text-gray-300" data-id="element-181">
                <p data-id="element-182">Entrás a tu barbería con claridad.</p>
                <p data-id="element-183">Sabés cuánto ganás. Sabés qué decisiones tomar.</p>
                <p data-id="element-184">Tu negocio deja de ser impredecible.</p>
                <p className="text-gray-400 mt-4 text-xs md:text-sm uppercase tracking-widest" data-id="element-185">
                  Y lo más importante:
                </p>
                <p className="text-xl md:text-2xl font-bold text-white mt-1" data-id="element-186">
                  👉 Empezás a generar ganancias reales.
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 12. TESTIMONIALS */}
      <section className="py-10 md:py-16 px-4" data-id="element-187">
        <div className="max-w-6xl mx-auto" data-id="element-188">
          <FadeIn data-id="element-189">
            <h2 className="text-3xl md:text-5xl font-bebas text-center text-white mb-6 md:mb-10" data-id="element-190">
              LO QUE DICEN <span className="text-gold" data-id="element-191">OTROS DUEÑOS</span>
            </h2>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-192">
            {TESTIMONIALS.map((testimonial, idx) => <FadeIn key={idx} delay={idx * 0.08} data-id="element-193">
                <div className="bg-dark-card border border-gold/20 p-4 md:p-6 rounded-xl h-full flex flex-col" data-id="element-194">
                  <div className="flex text-gold mb-2" data-id="element-195">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" data-id="element-196" />)}
                  </div>
                  <p className="text-sm md:text-base text-gray-300 italic mb-4 flex-grow" data-id="element-197">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-2 mt-auto" data-id="element-198">
                    <div className="w-8 h-8 bg-gold/20 rounded-full flex items-center justify-center border border-gold/50" data-id="element-199">
                      <User className="text-gold w-4 h-4" data-id="element-200" />
                    </div>
                    <div data-id="element-201">
                      <p className="font-bold text-white text-sm" data-id="element-202">
                        {testimonial.name}
                      </p>
                      <p className="text-xs text-gray-400 flex items-center gap-1" data-id="element-203">
                        <MapPin className="w-3 h-3" data-id="element-204" /> {testimonial.location}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 10. OFFER / PRICING */}
      <section ref={offerRef} id="purchase" className="py-10 md:py-16 px-4 relative" data-id="element-205">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold/5 to-transparent pointer-events-none" data-id="element-206" />

        <div className="max-w-3xl mx-auto relative z-10" data-id="element-207">
          <FadeIn data-id="element-208">
            <div className="text-center mb-6" data-id="element-209">
              <h2 className="text-4xl md:text-7xl font-bebas text-white mb-2" data-id="element-210">
                OFERTA
              </h2>
              <p className="text-base md:text-xl text-gray-300" data-id="element-211">
                Elegí tu acceso al KIT BARBERO PRO
              </p>
            </div>

            <div className="bg-gradient-to-b from-dark-card to-black border-2 border-gold rounded-3xl p-6 md:p-12 shadow-gold-glow-lg text-center relative overflow-hidden" data-id="element-212">
              <div className="absolute top-0 right-0 bg-gold text-black font-bold py-1 px-8 transform translate-x-8 translate-y-4 rotate-45 text-xs md:text-base" data-id="element-213">
                OFERTA HOY
              </div>

              <motion.img animate={{
              y: [0, -10, 0]
            }} transition={{
              repeat: Infinity,
              duration: 5,
              ease: 'easeInOut'
            }} src={PRODUCT_IMAGE} alt="Kit Barbero Pro" className="w-36 md:w-64 mx-auto mb-6 drop-shadow-gold-glow" data-id="element-214" />

              <h3 className="text-2xl md:text-4xl font-bebas text-white mb-3 flex items-center justify-center gap-2" data-id="element-215">
                📦 KIT BARBERO PRO
              </h3>
              <p className="text-sm md:text-lg text-gray-300 mb-6 max-w-md mx-auto" data-id="element-216">
                Sistema completo de gestión financiera para barberías
              </p>

              <div className="mb-6" data-id="element-217">
                <p className="text-gray-500 text-xl md:text-2xl line-through decoration-red-500/70 mb-1 font-bold" data-id="element-218">
                  $49.990
                </p>
                <p className="text-5xl md:text-7xl font-bebas text-gold drop-shadow-md" data-id="element-219">
                  $19.990
                </p>
              </div>

              <a href="https://checkout.sectordigital.online/cart/43876906532907:1" className="block w-full bg-gradient-to-r from-gold-dark via-gold to-gold-light text-black font-bold text-xl md:text-2xl py-5 px-6 rounded-xl shadow-gold-glow hover:shadow-gold-glow-lg transition-all transform hover:-translate-y-1 mb-4" data-id="element-220">
                👉 ACCEDER AHORA
              </a>

              <p className="text-gray-400 font-medium tracking-widest uppercase text-xs md:text-sm" data-id="element-221">
                Pago único · Acceso inmediato
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 13. FOOTER */}
      <footer className="py-8 px-4 border-t border-gray-900 bg-black text-center" data-id="element-222">
        <div className="max-w-2xl mx-auto" data-id="element-223">
          <h3 className="text-lg md:text-xl font-bebas text-gray-500 mb-2 tracking-widest" data-id="element-224">
            PRODUCTO DIGITAL
          </h3>
          <p className="text-gray-600 text-xs md:text-sm mb-1" data-id="element-225">
            Este producto es digital. Se envía automáticamente después de la
            compra.
          </p>
          <p className="text-gray-600 text-xs md:text-sm font-bold" data-id="element-226">
            Acceso inmediato.
          </p>
        </div>
      </footer>
    </div>;
}